/** Page-level layout helpers: PageHeader, Toolbar, ChartCard, SearchInput. */
import { Search } from 'lucide-react';
import type { InputHTMLAttributes, ReactNode } from 'react';

import { cn } from './cn.js';
import { Spinner } from './Feedback.js';

export function PageHeader({
  title,
  subtitle,
  actions,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  className?: string;
}): ReactNode {
  return (
    <div className={cn('flex flex-wrap items-end justify-between gap-3', className)}>
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-fg">{title}</h1>
        {subtitle ? <p className="mt-0.5 text-sm text-muted">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function Toolbar({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}): ReactNode {
  return <div className={cn('flex flex-wrap items-center gap-2', className)}>{children}</div>;
}

export function SearchInput({
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement>): ReactNode {
  return (
    <div className={cn('relative', className)}>
      <Search
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint"
        aria-hidden
      />
      <input
        className={
          'h-10 w-full rounded-lg border border-border bg-surface-2 pl-9 pr-3 text-sm text-fg ' +
          'placeholder:text-faint transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50'
        }
        {...rest}
      />
    </div>
  );
}

/** Card frame for a chart, with title, optional toolbar, and loading overlay. */
export function ChartCard({
  title,
  subtitle,
  toolbar,
  loading,
  empty,
  height = 280,
  className,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  toolbar?: ReactNode;
  loading?: boolean;
  empty?: ReactNode;
  height?: number;
  className?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div className={cn('rounded-xl border border-border bg-surface', className)}>
      <div className="flex items-start justify-between gap-3 px-5 pt-4">
        <div>
          <h3 className="font-display text-base font-semibold text-fg">{title}</h3>
          {subtitle ? <p className="text-xs text-muted">{subtitle}</p> : null}
        </div>
        {toolbar}
      </div>
      <div className="px-3 pb-3 pt-3" style={{ height }}>
        {loading ? (
          <div className="grid h-full place-items-center">
            <Spinner className="h-6 w-6 text-primary" />
          </div>
        ) : empty ? (
          <div className="grid h-full place-items-center text-sm text-muted">{empty}</div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}
